import React, { useState, useEffect, useRef } from 'react';
import { Container, Table, Row, Col, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { showToast } from '../../../utils/toast';

const AEPS = () => {
  const navigate = useNavigate();

  // Mock data for AEPS transactions
  const [data] = useState([
    {
      transactionDate: '2024-01-15',
      mobileNumber: '9876543210',
      aadhaarNumber: '****3611',
      amount: 5000,
      status: 'Success'
    },
    {
      transactionDate: '2024-01-16',
      mobileNumber: '9123456789',
      aadhaarNumber: '****2145',
      amount: 2500,
      status: 'Pending'
    },
    {
      transactionDate: '2024-01-17',
      mobileNumber: '9988776655',
      aadhaarNumber: '****7823',
      amount: 7500,
      status: 'Success'
    },
    {
      transactionDate: '2024-01-18',
      mobileNumber: '9876512345',
      aadhaarNumber: '****5634',
      amount: 1200,
      status: 'Failed'
    },
    {
      transactionDate: '2024-01-19',
      mobileNumber: '9123467890',
      aadhaarNumber: '****9087',
      amount: 3500,
      status: 'Success'
    }
  ]);

  // Form state
  const [aadhaarPart1, setAadhaarPart1] = useState('');
  const [aadhaarPart2, setAadhaarPart2] = useState('');
  const [aadhaarPart3, setAadhaarPart3] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedDevice, setSelectedDevice] = useState('');
  const [transactionType, setTransactionType] = useState('withdrawal');
  const [fingerprintCaptured, setFingerprintCaptured] = useState(false);
  const [amount, setAmount] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [showFundTransferPrompt, setShowFundTransferPrompt] = useState(false);
  const [isRetailerOnboarding, setIsRetailerOnboarding] = useState(true);
  const [showCustomerStageNotice, setShowCustomerStageNotice] = useState(false);
  const [receiptDetails, setReceiptDetails] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [retailerName, setRetailerName] = useState('');
  const [balanceResult, setBalanceResult] = useState(null);
  const [showMiniStatement, setShowMiniStatement] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [modalWithdrawalAmount, setModalWithdrawalAmount] = useState('');
  const [modalWithdrawalError, setModalWithdrawalError] = useState(false);
  const [amountError, setAmountError] = useState(false);
  const [deviceError, setDeviceError] = useState(false);
  const [bankError, setBankError] = useState(false);
  const [aadhaarError, setAadhaarError] = useState(false);
  const statementPrintRef = useRef(null);

  const brandLogoSrc = '/24-pay-logo.svg';
  const aepsLogoSrc = '/AEPS-Logo.png';

  // Banks list
  const banks = [
    'State Bank of India',
    'HDFC Bank',
    'ICICI Bank',
    'Axis Bank',
    'Punjab National Bank',
    'Bank of Baroda',
    'Canara Bank',
    'Union Bank of India'
  ];

  const mostUsedBanks = [
    'State Bank of India',
    'HDFC Bank',
    'ICICI Bank'
  ];

  const mostUsedAmounts = ['1000', '2000', '5000', '10000'];

  const balanceSummary = {
    accountNumber: 'XXXXXX1234',
    availableBalance: '₹45,760.00',
    lastUpdated: '11 Nov 2025, 02:45 PM'
  };

  const miniStatementData = [
    { date: '2024-01-18', description: 'ATM Cash Withdrawal', amount: '-₹1,500', balance: '₹24,500' },
    { date: '2024-01-16', description: 'IMPS Credit', amount: '+₹10,000', balance: '₹26,000' },
    { date: '2024-01-15', description: 'UPI Payment', amount: '-₹2,500', balance: '₹16,000' },
    { date: '2024-01-12', description: 'Utility Bill', amount: '-₹1,200', balance: '₹14,500' },
    { date: '2024-01-10', description: 'Salary Credit', amount: '+₹20,000', balance: '₹15,700' }
  ];

  const aadhaarLabel = isRetailerOnboarding ? 'Retailer Aadhaar Number' : 'Customer Aadhaar Number';

  // Biometric devices
  const biometricDevices = [
    { name: 'Mantra', image: '/devices/mantra.png' },
    { name: 'Morpho', image: '/devices/morpho.png' },
    { name: 'Startek', image: '/devices/startek.png' }
  ];

  // Persist selected device in localStorage
  useEffect(() => {
    try {
      const savedDevice = localStorage.getItem('selectedBiometricDevice');
      if (savedDevice) {
        setSelectedDevice(savedDevice);
      }
    } catch (error) {
      console.error('Failed to read biometric device from localStorage', error);
    }
  }, []);

  useEffect(() => {
    try {
      const storedName =
        localStorage.getItem('retailerName') ||
        localStorage.getItem('fullName') ||
        localStorage.getItem('name') ||
        '';
      setRetailerName(storedName || 'Retailer');
    } catch (error) {
      console.error('Failed to read retailer name from localStorage', error);
      setRetailerName('Retailer');
    }
  }, []);

  const handleSelectDevice = (deviceName) => {
    setSelectedDevice(deviceName);
    if (deviceName) {
      setDeviceError(false);
    }
    try {
      localStorage.setItem('selectedBiometricDevice', deviceName);
    } catch (error) {
      console.error('Failed to store biometric device in localStorage', error);
    }
  };

  const handleBankChange = (value) => {
    setSelectedBank(value);
    setShowFundTransferPrompt(false);
    setShowCustomerStageNotice(false);
    setShowReceiptModal(false);
    if (value) {
      setBankError(false);
    }
  };

  const handleAmountChange = (value) => {
    const sanitized = value.replace(/\D/g, '').slice(0, 7);
    setAmount(sanitized);
    setShowFundTransferPrompt(false);
    setShowCustomerStageNotice(false);
    setShowReceiptModal(false);
    if (amountError) {
      setAmountError(false);
    }
  };

  const handleMostUsedAmountClick = (value) => {
    setAmount(value);
    setShowFundTransferPrompt(false);
    setShowCustomerStageNotice(false);
    setShowReceiptModal(false);
    if (amountError) {
      setAmountError(false);
    }
  };

  const handleModalAmountChange = (value) => {
    const sanitized = value.replace(/\D/g, '').slice(0, 7);
    setModalWithdrawalAmount(sanitized);
    if (modalWithdrawalError) {
      setModalWithdrawalError(false);
    }
  };

  const handleModalWithdrawal = () => {
    if (!modalWithdrawalAmount || Number(modalWithdrawalAmount) <= 0) {
      setModalWithdrawalError(true);
      showToast.warning('Please enter a valid withdrawal amount');
      return;
    }
    setTransactionType('withdrawal');
    setAmount(modalWithdrawalAmount);
    setShowBalanceModal(false);
    setShowStatementModal(false);
    setModalWithdrawalAmount('');
    setModalWithdrawalError(false);
    setBalanceResult(null);
    setShowMiniStatement(false);
    showToast.info('Please Select Bank');
  };

  // Column definitions
  const columns = [
    { key: 'transactionDate', label: 'Date' },
    { key: 'mobileNumber', label: 'Mobile Number' },
    { key: 'aadhaarNumber', label: 'Aadhaar Number' },
    { key: 'amount', label: 'Amount' },
    { key: 'status', label: 'Status' }
  ];

  // Handle aadhaar input
  const handleAadhaarInput = (part, value, setter, nextRef) => {
    const numericValue = value.replace(/\D/g, '');
    if (numericValue.length <= 4) {
      setter(numericValue);
      setShowFundTransferPrompt(false);
      setShowCustomerStageNotice(false);
      // Auto focus next input
      if (numericValue.length === 4 && nextRef) {
        document.getElementById(nextRef)?.focus();
      }
      const updatedPart1 = part === 'part1' ? numericValue : aadhaarPart1;
      const updatedPart2 = part === 'part2' ? numericValue : aadhaarPart2;
      const updatedPart3 = part === 'part3' ? numericValue : aadhaarPart3;
      if (
        updatedPart1.length === 4 &&
        updatedPart2.length === 4 &&
        updatedPart3.length === 4
      ) {
        setAadhaarError(false);
      }
    }
  };

  // Handle capture fingerprint
  const validateForm = () => {
    let isValid = true;
    if (!aadhaarPart1 || !aadhaarPart2 || !aadhaarPart3) {
      setAadhaarError(true);
      showToast.error('Please enter complete Aadhaar number');
      isValid = false;
    } else {
      setAadhaarError(false);
    }
    if (!isRetailerOnboarding && transactionType === 'withdrawal') {
      if (!selectedBank) {
        setBankError(true);
        // showToast.error('Please select a bank');
        isValid = false;
      } else {
        setBankError(false);
      }
      if (!amount || Number(amount) <= 0) {
        setAmountError(true);
        // showToast.warning('Please enter a valid amount');
        isValid = false;
      }
    }
    return isValid;
  };

  const handleSubmit = (skipFingerprintCheck = false) => {
    if (!skipFingerprintCheck && !fingerprintCaptured) {
      showToast.warning('Please capture fingerprint first');
      return;
    }

    if (isRetailerOnboarding) {
      showToast.success('Retailer onboarding completed successfully!');
      setAadhaarPart1('');
      setAadhaarPart2('');
      setAadhaarPart3('');
      setFingerprintCaptured(false);
      setIsRetailerOnboarding(false);
      setShowCustomerStageNotice(true);
      setReceiptDetails(null);
      setShowReceiptModal(false);
      setBalanceResult(null);
      setShowMiniStatement(false);
      setAadhaarError(false);
      setBankError(false);
      setDeviceError(false);
      return;
    }

    if (transactionType === 'balance') {
      const fetchedAt = new Date().toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
      });
      setBalanceResult({ ...balanceSummary, fetchedAt });
      setShowMiniStatement(false);
      setReceiptDetails(null);
      setShowReceiptModal(false);
      setShowBalanceModal(true);
      setShowStatementModal(false);
      setFingerprintCaptured(false);
      // showToast.success('Balance fetched successfully!');
      return;
    } else if (transactionType === 'statement') {
      setBalanceResult(null);
      setShowMiniStatement(true);
      setReceiptDetails(null);
      setShowReceiptModal(false);
      setShowBalanceModal(false);
      setShowStatementModal(true);
      setFingerprintCaptured(false);
      showToast.success('Mini statement fetched successfully!');
      return;
    } else {
      if (!selectedBank) {
        setBankError(true);
        // showToast.error('Please select a bank');
        setFingerprintCaptured(false);
        return;
      }
      if (!amount || Number(amount) <= 0) {
        // showToast.warning('Please enter a valid amount');
        setFingerprintCaptured(false);
        setAmountError(true);
        return;
      }

      const bankRefNumber = `BRN${Date.now().toString().slice(-8)}`;
      const maskedAadhaar = `XXXX-XXXX-${aadhaarPart3}`;

      const receipt = {
        transactionId: `AEPS${Date.now()}`,
        bankRefNumber,
        timestamp: new Date().toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: true
        }),
        bank: selectedBank,
        aadhaar: maskedAadhaar,
        amount: Number(amount),
        device: selectedDevice,
        status: 'Success',
        retailerName: retailerName || 'Retailer'
      };

      setReceiptDetails(receipt);
      setShowReceiptModal(true);
      // showToast.transactionSuccess('AEPS transaction submitted successfully!');
      setBalanceResult(null);
      setShowMiniStatement(false);
    }

    setAadhaarPart1('');
    setAadhaarPart2('');
    setAadhaarPart3('');
    setFingerprintCaptured(false);
    setAmount('');
    setAmountError(false);
    setModalWithdrawalAmount('');
    setShowFundTransferPrompt(true);
    setIsRetailerOnboarding(true);
    setShowCustomerStageNotice(false);
    setShowBalanceModal(false);
    setShowStatementModal(false);
    setDeviceError(false);
    setBankError(false);
    setAadhaarError(false);
  };

  const handleCaptureFingerprint = () => {
    if (!selectedDevice) {
      showToast.warning('Please select a biometric device first');
      setDeviceError(true);
      return;
    }
    if (isCapturing) {
      return;
    }
    if (!validateForm()) {
      return;
    }

    setIsCapturing(true);
    setFingerprintCaptured(false);

    setTimeout(() => {
      setFingerprintCaptured(true);
      showToast.success('Fingerprint captured successfully!');
      handleSubmit(true);
      setIsCapturing(false);
    }, 1500);
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleCloseReceiptModal = () => {
    setShowReceiptModal(false);
  };

  const handleFundTransferNavigate = () => {
    navigate('/retailer/fund-transfer');
    setShowFundTransferPrompt(false);
  };

  const handleCloseBalanceModal = () => {
    setShowBalanceModal(false);
    setBalanceResult(null);
    setModalWithdrawalAmount('');
    setModalWithdrawalError(false);
  };

  const handleCloseStatementModal = () => {
    setShowStatementModal(false);
    setShowMiniStatement(false);
    setModalWithdrawalAmount('');
    setModalWithdrawalError(false);
  };

  const handlePrintMiniStatement = () => {
    if (!statementPrintRef.current) {
      showToast.warning('Mini statement data is not available to print right now.');
      return;
    }

    const printContents = statementPrintRef.current.innerHTML;
    const printWindow = window.open('', '_blank', 'width=900,height=650');

    if (!printWindow) {
      showToast.warning('Please allow pop-ups to print the mini statement.');
      return;
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Mini Statement</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; background: #f8fafc; color: #0f172a; }
            h1, h2, h3, h4, h5, h6 { margin: 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            th, td { border: 1px solid #bfdbfe; padding: 8px; text-align: left; font-size: 13px; }
            th { background: #dbeafe; }
            .details { display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 16px; }
            .details div { min-width: 200px; }
            .badge-success { display: inline-block; background: #22c55e; color: #fff; padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; }
          </style>
        </head>
        <body>
          ${printContents}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Get status badge variant
  const getStatusVariant = (status) => {
    switch(status) {
      case 'Success': return 'success';
      case 'Pending': return 'warning';
      case 'Failed': return 'danger';
      default: return 'secondary';
    }
  };

  return (
    <Container fluid className="p-4">
      <div className="user-table-container">
        {/* Header with Title */}
        <Row className="mb-4">
          <Col>
            <h4 className="mb-0" style={{ color: '#1e293b', fontWeight: '700', fontSize: '1.5rem' }}>
              <i className="bi bi-fingerprint me-2"></i>
              {isRetailerOnboarding ? 'AEPS Onboarding' : 'AEPS Transaction'}
            </h4>
          </Col>
        </Row>

        <Row className="g-4">
          <Col lg={5} xs={12}>
            <div
              style={{
                background: '#f8f9fa',
                padding: '1.25rem',
                borderRadius: '10px',
                border: '1px solid #e2e8f0',
                height: '100%'
              }}
            >
              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600', color: '#1e293b', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  Select Biometric Device
                  <span style={{ color: '#dc2626', marginLeft: '0.25rem' }}>*</span>
                </Form.Label>
                <Form.Select
                  value={selectedDevice}
                  onChange={(e) => handleSelectDevice(e.target.value)}
                  style={{ fontSize: '0.95rem', padding: '0.5rem' }}
                  isInvalid={deviceError}
                >
                  <option value="">Select</option>
                  {biometricDevices.map((device) => (
                    <option key={device.name} value={device.name}>
                      {device.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  Biometric device is required.
                </Form.Control.Feedback>
              </Form.Group>

              {!isRetailerOnboarding && transactionType === 'withdrawal' && (
                <>
                  <Form.Group className="mb-2">
                    <Form.Label style={{ fontWeight: '600', color: '#1e293b', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                      Bank
                      <span style={{ color: '#dc2626', marginLeft: '0.25rem' }}>*</span>
                    </Form.Label>
                    <Form.Select
                      value={selectedBank}
                      onChange={(e) => handleBankChange(e.target.value)}
                      style={{ fontSize: '0.95rem', padding: '0.5rem' }}
                      isInvalid={bankError}
                    >
                      <option value="">Select</option>
                      {banks.map((bank, index) => (
                        <option key={index} value={bank}>{bank}</option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Bank selection is required.
                    </Form.Control.Feedback>
                  </Form.Group>

                  {mostUsedBanks.length > 0 && (
                    <div className="mb-3">
                      <div style={{ marginBottom: '0.4rem', fontWeight: 600, color: '#334155', fontSize: '0.85rem' }}>
                        Frequently Used Banks
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {mostUsedBanks.map((bank) => (
                          <Badge
                            key={bank}
                            bg={selectedBank === bank ? 'primary' : 'light'}
                            text={selectedBank === bank ? 'light' : 'dark'}
                            style={{
                              cursor: 'pointer',
                              padding: '0.45rem 0.75rem',
                              borderRadius: '999px',
                              fontWeight: 600,
                              boxShadow: selectedBank === bank ? '0 2px 6px rgba(37, 99, 235, 0.3)' : 'none',
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => setSelectedBank(bank)}
                          >
                            {bank}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {!isRetailerOnboarding && (
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#1e293b', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Transaction Type
                  </Form.Label>
                  <Form.Select
                    value={transactionType}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTransactionType(value);
                      setAmount('');
                      setBalanceResult(null);
                      setShowMiniStatement(false);
                      setBankError(false);
                      setAmountError(false);
                    }}
                    style={{ fontSize: '0.95rem', padding: '0.5rem' }}
                  >
                    <option value="withdrawal">Withdrawal</option>
                    <option value="balance">Balance Check</option>
                    <option value="statement">Mini Statement</option>
                  </Form.Select>
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600', color: '#1e293b', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                  {aadhaarLabel}
                  <span style={{ color: '#dc2626', marginLeft: '0.25rem' }}>*</span>
                </Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    id="aadhaar-part1"
                    type="text"
                    placeholder="xxxx"
                    value={aadhaarPart1}
                    onChange={(e) => handleAadhaarInput('part1', e.target.value, setAadhaarPart1, 'aadhaar-part2')}
                    maxLength={4}
                    style={{
                      fontSize: '0.95rem',
                      textAlign: 'center',
                      fontWeight: '500',
                      letterSpacing: '1px',
                      padding: '0.5rem'
                    }}
                  />
                  <Form.Control
                    id="aadhaar-part2"
                    type="text"
                    placeholder="xxxx"
                    value={aadhaarPart2}
                    onChange={(e) => handleAadhaarInput('part2', e.target.value, setAadhaarPart2, 'aadhaar-part3')}
                    maxLength={4}
                    style={{
                      fontSize: '0.95rem',
                      textAlign: 'center',
                      fontWeight: '500',
                      letterSpacing: '1px',
                      padding: '0.5rem'
                    }}
                  />
                  <Form.Control
                    id="aadhaar-part3"
                    type="text"
                    placeholder="xxxx"
                    value={aadhaarPart3}
                    onChange={(e) => handleAadhaarInput('part3', e.target.value, setAadhaarPart3, null)}
                    maxLength={4}
                    style={{
                      fontSize: '0.95rem',
                      textAlign: 'center',
                      fontWeight: '500',
                      letterSpacing: '1px',
                      padding: '0.5rem'
                    }}
                  />
                </div>
                {aadhaarError && (
                  <div style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.35rem' }}>
                    Complete Aadhaar number is required.
                  </div>
                )}
              </Form.Group>

              {!isRetailerOnboarding && transactionType === 'withdrawal' && (
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#1e293b', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                    Amount
                    <span style={{ color: '#dc2626', marginLeft: '0.25rem' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    style={{
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      padding: '0.5rem'
                    }}
                    isInvalid={amountError}
                  />
                  <Form.Control.Feedback type="invalid" style={{ fontSize: '0.75rem' }}>
                    Withdrawal amount is required.
                  </Form.Control.Feedback>
                  {mostUsedAmounts.length > 0 && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', marginBottom: '0.35rem' }}>
                        Frequently Used Amounts
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {mostUsedAmounts.map((value) => (
                          <Badge
                            key={value}
                            bg={amount === value ? 'success' : 'light'}
                            text={amount === value ? 'light' : 'dark'}
                            style={{
                              cursor: 'pointer',
                              padding: '0.45rem 0.75rem',
                              borderRadius: '999px',
                              fontWeight: 600,
                              boxShadow: amount === value ? '0 2px 6px rgba(34, 197, 94, 0.3)' : 'none',
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => handleMostUsedAmountClick(value)}
                          >
                            ₹{Number(value).toLocaleString()}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </Form.Group>
              )}

              <Button
                onClick={handleCaptureFingerprint}
                disabled={!selectedDevice || isCapturing}
                style={{
                  width: '80px',
                  height: '80px',
                  background: isCapturing ? '#0d6efd' : '#ffc107',
                  border: 'none',
                  color: '#1e293b',
                  borderRadius: '10px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                aria-label="Capture and submit fingerprint"
              >
                {isCapturing ? (
                  <Spinner
                    animation="border"
                    role="status"
                    style={{ width: '36px', height: '36px' }}
                  />
                ) : (
                  <i className="bi bi-fingerprint" style={{ fontSize: '2rem' }}></i>
                )}
              </Button>

            </div>
          </Col>

          <Col lg={7} xs={12}>
            <div className="table-responsive" style={{ background: '#f0f2f5', borderRadius: '12px', padding: '0' }}>
              <Table bordered hover className="modern-table" style={{ background: '#f0f2f5' }}>
                <thead style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <tr>
                    {columns.map((column) => (
                      <th key={column.key}>{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.length > 0 ? (
                    data.map((row, index) => (
                      <tr key={index} style={{ background: '#f0f2f5' }}>
                        <td>{row.transactionDate}</td>
                        <td>{row.mobileNumber}</td>
                        <td>{row.aadhaarNumber}</td>
                        <td><strong>₹{row.amount.toLocaleString()}</strong></td>
                        <td>
                          <Badge bg={getStatusVariant(row.status)}>
                            {row.status}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={columns.length} className="text-center py-4">
                        <i className="bi bi-inbox fs-1 text-muted d-block mb-2"></i>
                        <span className="text-muted">No transactions found</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>

        <Modal show={showBalanceModal && !!balanceResult} onHide={handleCloseBalanceModal} centered size="md">
          <Modal.Header closeButton>
            <Modal.Title as="div" style={{ width: '100%' }}>
              <div className="d-flex justify-content-between align-items-center">
                <img src={brandLogoSrc} alt="Brand Logo" style={{ height: '36px', objectFit: 'contain' }} />
                <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '1.1rem' }}>Balance Summary</span>
                <img src={aepsLogoSrc} alt="AEPS Logo" style={{ height: '36px', objectFit: 'contain' }} />
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: '#f8fafc' }}>
            {balanceResult && (
              <div
                style={{
                  background: '#ffffff',
                  border: '1px solid #d9f99d',
                  borderRadius: '12px',
                  padding: '1.2rem',
                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.15)',
                  fontSize: '0.78rem'
                }}
              >
                <Row className="mb-3">
                  <Col md={6} className="mb-3 mb-md-0">
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                      Agent ID
                    </div>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.78rem' }}>SDL408898</div>
                  </Col>
                  <Col md={6}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                      Agent Name
                    </div>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.78rem' }}>{retailerName || 'Retailer'}</div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6} className="mb-3 mb-md-0">
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                      Bank Name
                    </div>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.78rem' }}>State Bank of India</div>
                  </Col>
                  <Col md={6}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                      Status
                    </div>
                    <Badge bg="success" style={{ fontWeight: 600, fontSize: '0.7rem' }}>Success</Badge>
                  </Col>
                </Row>

                <div style={{ borderTop: '1px dashed #d1d5db', margin: '1rem 0' }}></div>

                <Row className="mb-3">
                  <Col md={6}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>
                      Available Balance (INR)
                    </div>
                    <div style={{ fontWeight: 800, fontSize: '1.1rem', color: '#16a34a' }}>
                      {balanceResult.availableBalance}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500 }}>
                      Account: {balanceResult.accountNumber}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>
                      Date &amp; Time
                    </div>
                    <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.78rem' }}>
                      {balanceResult.fetchedAt || balanceSummary.lastUpdated}
                    </div>
                    <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500, marginTop: '0.25rem' }}>
                      Response Code: 00
                    </div>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.8rem' }}>
                    Withdrawal Amount
                    <span style={{ color: '#dc2626', marginLeft: '0.25rem' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter amount"
                    value={modalWithdrawalAmount}
                    onChange={(e) => handleModalAmountChange(e.target.value)}
                    style={{ fontWeight: 600, fontSize: '0.8rem' }}
                    isInvalid={modalWithdrawalError}
                  />
                  <Form.Control.Feedback type="invalid" style={{ fontSize: '0.7rem' }}>
                    Withdrawal amount is required.
                  </Form.Control.Feedback>
                </Form.Group>

                <Button variant="warning" onClick={handleModalWithdrawal} style={{ fontWeight: 600, fontSize: '0.8rem' }}>
                  Proceed to Withdrawal
                </Button>
              </div>
            )}
          </Modal.Body>
        </Modal>

        <Modal show={showStatementModal} onHide={handleCloseStatementModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title as="div" style={{ width: '100%' }}>
              <div className="d-flex justify-content-between align-items-center">
                <img src={brandLogoSrc} alt="Brand Logo" style={{ height: '36px', objectFit: 'contain' }} />
                <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '1.1rem' }}>Mini Statement</span>
                <img src={aepsLogoSrc} alt="AEPS Logo" style={{ height: '36px', objectFit: 'contain' }} />
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: '#f8fafc' }}>
            <div
              ref={statementPrintRef}
              style={{
                background: '#ffffff',
                border: '1px solid #bfdbfe',
                borderRadius: '12px',
                padding: '1.5rem',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
              }}
            >
              <Row className="mb-3" style={{ fontSize: '0.75rem' }}>
                <Col md={6} className="mb-3 mb-md-0">
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                    Agent ID
                  </div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.78rem' }}>SDL408898</div>
                </Col>
                <Col md={6}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                    Agent Name
                  </div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.78rem' }}>{retailerName || 'Retailer'}</div>
                </Col>
              </Row>

              <Row className="mb-3" style={{ fontSize: '0.75rem' }}>
                <Col md={6} className="mb-3 mb-md-0">
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                    Bank Name
                  </div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.78rem' }}>State Bank of India</div>
                </Col>
                <Col md={6}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                    Status
                  </div>
                  <Badge bg="success" style={{ fontWeight: 600 }}>Success</Badge>
                </Col>
              </Row>

              <Row className="mb-3" style={{ fontSize: '0.75rem' }}>
                <Col md={6} className="mb-3 mb-md-0">
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                    Aadhaar Number
                  </div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.78rem' }}>
                    XXXX-XXXX-{aadhaarPart3 || '1466'}
                  </div>
                </Col>
                <Col md={6}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                    Date &amp; Time
                  </div>
                  <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.78rem' }}>
                    {new Date().toLocaleString('en-IN', {
                      timeZone: 'Asia/Kolkata',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                      hour12: true
                    })}
                  </div>
                </Col>
              </Row>

              <div className="table-responsive mb-4">
                <Table bordered size="sm" style={{ marginBottom: 0, background: '#f8fafc', fontSize: '0.78rem' }}>
                  <thead style={{ background: '#dbeafe', color: '#1e293b', fontSize: '0.75rem' }}>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {miniStatementData.map((item, index) => (
                      <tr key={index} style={{ fontSize: '0.78rem' }}>
                        <td style={{ fontSize: '0.75rem' }}>{item.date}</td>
                        <td style={{ fontSize: '0.75rem' }}>{item.description}</td>
                        <td style={{ fontSize: '0.75rem' }}>{item.amount}</td>
                        <td style={{ fontSize: '0.75rem' }}>{item.balance}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.8rem' }}>
                  Withdrawal Amount
                  <span style={{ color: '#dc2626', marginLeft: '0.25rem' }}>*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter amount"
                  value={modalWithdrawalAmount}
                  onChange={(e) => handleModalAmountChange(e.target.value)}
                  style={{ fontWeight: 600, fontSize: '0.8rem' }}
                  isInvalid={modalWithdrawalError}
                />
                <Form.Control.Feedback type="invalid" style={{ fontSize: '0.7rem' }}>
                  Withdrawal amount is required.
                </Form.Control.Feedback>
              </Form.Group>

              <div className="d-flex flex-column flex-sm-row justify-content-end gap-2">
                <Button
                  variant="outline-primary"
                  onClick={handlePrintMiniStatement}
                  style={{ fontWeight: 600, fontSize: '0.8rem' }}
                >
                  <i className="bi bi-printer me-2"></i>
                  Print
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCloseStatementModal}
                  style={{ fontWeight: 600, fontSize: '0.8rem' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="warning"
                  onClick={handleModalWithdrawal}
                  style={{ fontWeight: 600, fontSize: '0.8rem' }}
                >
                  Withdrawal
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        <Modal show={showReceiptModal && !!receiptDetails} onHide={handleCloseReceiptModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title
              as="div"
              style={{ fontWeight: 700, color: '#1e293b' }}
            >
              <div className="d-flex align-items-center gap-2">
                <img
                  src={brandLogoSrc}
                  alt="Brand Logo"
                  style={{ height: '40px', objectFit: 'contain' }}
                />
                <span>AEPS Payment Receipt</span>
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ background: '#f8fafc' }}>
            {receiptDetails && (
              <div
                style={{
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '1.5rem',
                  boxShadow: '0 4px 12px rgba(148, 163, 184, 0.15)'
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <div style={{ color: '#1e293b', fontWeight: 700, fontSize: '1.1rem' }}>
                      AEPS Transaction Receipt
                    </div>
                    <div style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 500 }}>
                      Secure transaction details for your records
                    </div>
                  </div>
                  <img
                    src={aepsLogoSrc}
                    alt="AEPS Logo"
                    style={{ height: '48px', objectFit: 'contain' }}
                  />
                </div>

                <Row className="mb-3">
                  <Col>
                    <div style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600 }}>
                      Transaction Reference
                    </div>
                    <h5 style={{ fontWeight: 700, color: '#1e293b', marginBottom: '0.25rem' }}>
                      {receiptDetails.transactionId}
                    </h5>
                    <div style={{ color: '#64748b', fontSize: '0.85rem', fontWeight: 600, marginTop: '0.75rem' }}>
                      Bank Reference Number
                    </div>
                    <div style={{ color: '#1e293b', fontWeight: 700, fontSize: '0.95rem' }}>
                      {receiptDetails.bankRefNumber}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <div style={{ color: '#334155', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      Transaction Date & Time
                    </div>
                    <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '0.95rem' }}>
                      {receiptDetails.timestamp}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div style={{ color: '#334155', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      Status
                    </div>
                    <Badge bg="success" style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}>
                      {receiptDetails.status}
                    </Badge>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <div style={{ color: '#334155', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      Retailer Name
                    </div>
                    <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '0.95rem' }}>
                      {receiptDetails.retailerName}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div style={{ color: '#334155', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      Aadhaar Number
                    </div>
                    <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '0.95rem', letterSpacing: '1px' }}>
                      {receiptDetails.aadhaar}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <div style={{ color: '#334155', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      Bank
                    </div>
                    <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '0.95rem' }}>
                      {receiptDetails.bank}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-0">
                  <Col md={6}>
                    <div style={{ color: '#334155', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      Biometric Device
                    </div>
                    <div style={{ color: '#0f172a', fontWeight: 700, fontSize: '0.95rem' }}>
                      {receiptDetails.device}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div style={{ color: '#334155', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      Transaction Amount
                    </div>
                    <div style={{ color: '#047857', fontWeight: 800, fontSize: '1.1rem' }}>
                      ₹{receiptDetails.amount.toLocaleString('en-IN')}
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handlePrintReceipt}>
              <i className="bi bi-printer me-1"></i>
              Print Receipt
            </Button>
            <Button variant="secondary" onClick={handleCloseReceiptModal}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Container>
  );
};

export default AEPS;

